import { BaseSelectableComponent } from "../../baseComponent.mjs";
import { Dropdown } from "../../elements/dropdown.mjs";
import { PhIcon, iconComponentFor } from "../../elements/ph-icon.mjs";
import { ToolbarItem } from "./toolbarItem.mjs";
import van from "../../../vanjs.mjs";

/**
 * @class ToolbarChoiceGroup
 * @extends BaseSelectableComponent
 * @description A compact radio group implemented as a single Dropdown whose
 * header always shows the currently selected tool (icon + tooltip). Parents
 * treat it as a single selectable "slot" via setActiveInParent(active).
 *
 * Children must be ToolbarItem instances.
 */
class ToolbarChoiceGroup extends BaseSelectableComponent {
    constructor(options = undefined, ...children) {
        options = super(options, ...children).options;
        // logical ID of the selected item (uses child.itemID || child.id)
        this._selectedId = van.state(this.options.defaultSelected ?? null);
        this._dropdown = null;      // Dropdown instance
        this._items = [];           // [{id,itemID,label,icon,_childItem}]
    }

    create() {
        const childItems = this._children.filter(c => c instanceof ToolbarItem);

        const defaultKey = this.options.defaultSelected;
        const defaultItem =
            (defaultKey != null
                    ? (
                        childItems.find(c => (c.options.itemID ?? c.id) === defaultKey)
                        || childItems.find(c => c.id === defaultKey)
                    )
                    : null
            ) || childItems[0];

        if (!defaultItem) {
            console.warn(`ToolbarChoiceGroup (${this.id}) has no items.`);
            return document.createElement("div");
        }

        const defaultItemKey = defaultItem.options.itemID ?? defaultItem.id;
        this._selectedId.val = defaultItemKey;

        // icon name from child — an icon component exposes the glyph name
        // under options.name, a plain string is already the name
        const childIconName = (ci) => (ci.options.icon instanceof PhIcon)
            ? ci.options.icon.options.name
            : ci.options.icon;

        const defaultIconName = childIconName(defaultItem);

        // normalize items so we can map between logical IDs and ToolbarItems
        this._items = childItems.map(ci => {
            const key = ci.options.itemID ?? ci.id;
            return {
                id: key,
                itemID: key,
                label: ci.options.label,
                tooltip: ci.options.tooltip,
                icon: childIconName(ci),
                _childItem: ci
            };
        });

        const headerIcon = iconComponentFor(defaultIconName);

        // single dropdown; header icon will be driven by activeSelection
        this._dropdown = new Dropdown({
            id: this.id,
            icon: headerIcon,
            title: defaultItem.options.tooltip ?? defaultItem.options.label,
            items: this._items.map(item => ({
                id: item.itemID,
                itemID: item.itemID,
                label: item.label,
                title: item.tooltip ?? item.label,
                icon: item.icon,
                onClick: (e, data) => {
                    // user picked a new item from the menu
                    this.setSelected(data.itemID ?? data.id, true, true);
                }
            })),
            activeSelection: defaultItemKey, // header tracks selection
            closeOnItemClick: true,
            placement: "below",

            // --- new: split header behaviour ---
            splitHeader: true,
            onHeaderMainClick: () => {
                // Icon click → re-trigger current selection logic
                const currentId = this._selectedId.val;
                if (currentId) {
                    this.setSelected(currentId, true, true);
                }
            }
            // onHeaderArrowClick not needed – default is just "open menu"
        });

        const el = this._dropdown.create();

        // make the header look like a toolbar button
        this._dropdown.headerButton.setClass(
            "base",
            "btn btn-square btn-sm join-item"
        );
        this._dropdown.iconOnly(); // only show the icon; tooltip has the label

        const headerId = this._dropdown.headerButton.id;

        // adapt the header to toolbar orientation. Horizontal keeps the roomy
        // icon + caret pair (58px, set by Dropdown.iconOnly). Vertical collapses
        // it to a plain square icon button with the caret as a corner marker —
        // otherwise this one control drags the whole column out to 58px, since
        // every sibling item stretches to `w-full`.
        queueMicrotask(() => {
            const root = el.closest("[data-toolbar-root]");
            if (!root) return;

            const apply = (dir) => {
                const btnEl = document.getElementById(headerId);
                if (!btnEl) return;

                const vertical = dir === "vertical";
                btnEl.classList.remove("w-full");
                // vertical: one fixed column width shared with every other
                // control; horizontal: the intrinsic square + the 58px header
                btnEl.classList.toggle("toolbar-btn-vertical", vertical);
                btnEl.classList.toggle("btn-square", !vertical);
                this._dropdown.setCompactHeader(vertical);
            };

            const handler = (e) => apply(e.detail.dir);

            root.addEventListener("toolbar:measure", handler);
            // initial orientation
            apply(root.classList.contains("flex-col") ? "vertical" : "horizontal");
        });

        return el;
    }

    /**
     * Programmatic selection of an internal item.
     * @param {string} id logical item ID (child.options.itemID or child.id)
     * @param {boolean} [fireOnChange=true] whether to call this.options.onChange
     * @param {boolean} [fireChildClick=false] whether to call underlying ToolbarItem.onClick
     */
    setSelected(id, fireOnChange = true, fireChildClick = false) {
        const item = this._items.find(i => i.itemID === id || i.id === id);
        if (!item) {
            return;
        }

        const key = item.itemID || item.id;
        this._selectedId.val = key;

        if (this._dropdown) {
            // Dropdown.setSelected handles header icon/title + list highlight
            this._dropdown.setSelected(key);
        }

        if (fireOnChange) {
            this.options.onChange?.(key);
        }
        if (fireChildClick && item._childItem?.options.onClick) {
            item._childItem.options.onClick();
        }
    }

    /**
     * Used by parent ToolbarGroup to show this slot as active.
     * Does NOT change which internal item is selected.
     */
    setActiveInParent(active) {
        if (!this._dropdown) return;
        const btnEl = document.getElementById(this._dropdown.headerButton.id);
        if (!btnEl) return;
        btnEl.classList.toggle("btn-active", !!active);
        btnEl.classList.toggle("btn-primary", !!active);
    }
}

export { ToolbarChoiceGroup };
